import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DEFAULT_ACCENT } from "@prism/shared/constants";
import { applyTheme } from "../lib/theme.js";
import { apiFetch } from "../lib/api.js";
import { loadSession, saveSession } from "../lib/storage.js";

const AuthContext = createContext(null);

/**
 * AuthProvider
 *
 * Holds the authenticated session in state + localStorage.
 * - On mount: rehydrates the session (if any) and reapplies the theme.
 *   (main.jsx also applies theme before first paint — this call keeps
 *   things consistent if accentColor changes mid-session.)
 * - login(email, password): POST /api/auth/login, stores token+user+client.
 * - logout(): clears session and resets theme to the default.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => loadSession());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    applyTheme(session?.client?.accentColor || DEFAULT_ACCENT);
  }, [session?.client?.accentColor]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const body = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      const next = { token: body.token, user: body.user, client: body.client };
      saveSession(next);
      setSession(next);
      return next;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    saveSession(null);
    setSession(null);
    // Theme reset happens via the useEffect above when session.client becomes null.
  }, []);

  const value = {
    token: session?.token || null,
    user: session?.user || null,
    client: session?.client || null,
    authenticated: !!session?.token,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
